'use client';

import { Button, DropdownMenu, Input } from '@common/components';

import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';

import type {
  HouseholdSize,
  MedianIncomeState,
} from '../_hooks/useMedianIncomeState';
import { HOUSEHOLD_OPTIONS } from '../constants';

interface CalculatorProps {
  state: MedianIncomeState;
  /** 상세 페이지 등에서 promo 용도로 사용할 때 하단 "내 중위소득 확인하기 →" CTA 노출. */
  showNavCta?: boolean;
}

/**
 * ② 중위소득 계산기 위젯 — 가구원수 / 월소득 / 비율 3-way 입력.
 *
 * - 상위에서 `useMedianIncomeState` 의 반환값을 그대로 주입.
 * - 라벨 좌측 + input 우측 가로 row 배치 + DS 최소 dimension(H40) — 모바일 sticky 와 동일 룩.
 * - `showNavCta` 가 true 면 하단에 `/median-income` 으로 이동하는 CTA 버튼 노출.
 */
export const Calculator = ({ state, showNavCta }: CalculatorProps) => {
  const {
    householdSize,
    amountInput,
    percentInput,
    setHouseholdSize,
    onAmountChange,
    onPercentChange,
  } = state;

  return (
    <div className="flex flex-col gap-12 rounded-medium border-1 border-Gray-100 bg-Gray-white p-20">
      <h2 className="font-700 text-Gray-900 typo-body2">중위소득 계산기</h2>

      <div className="flex flex-col">
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-12">
          <label
            htmlFor="median-income-pct"
            className="whitespace-nowrap text-Gray-700 typo-caption1"
          >
            중위소득 비율
          </label>
          <Input
            id="median-income-pct"
            type="text"
            inputMode="decimal"
            dimensions="H40"
            className="border-transparent bg-transparent"
            value={percentInput}
            onChange={(e) => onPercentChange(e.target.value)}
            placeholder="예: 100"
            rightContent={
              <span className="font-400 text-Gray-600 typo-caption1">%</span>
            }
          />
        </div>
        <hr className="border-t border-Gray-200" />
      </div>

      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-12 gap-y-8">
        <label
          htmlFor="median-income-hh"
          className="whitespace-nowrap text-Gray-700 typo-caption1"
        >
          가구원
        </label>
        <DropdownMenu
          dimensions="H40"
          className="!min-w-0"
          options={HOUSEHOLD_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          value={householdSize}
          onChange={(v) => setHouseholdSize(v as HouseholdSize)}
          placeholder="가구원 수 선택"
        />
        <label
          htmlFor="median-income-amount"
          className="whitespace-nowrap text-Gray-700 typo-caption1"
        >
          가구원 월소득
        </label>
        <Input
          id="median-income-amount"
          type="local-string-number"
          inputMode="numeric"
          dimensions="H40"
          value={amountInput}
          onChange={(e) => onAmountChange(e.target.value)}
          placeholder="예: 300"
          rightContent={
            <span className="font-400 text-Gray-600 typo-caption1">만원</span>
          }
        />
      </div>

      {showNavCta ? (
        <Button
          asChild
          theme="primary-fill"
          dimensions="H48"
          className="w-full"
        >
          <CustomLink
            href={`${ROUTES.MEDIAN_INCOME.pathname}?household=${householdSize}&percent=${encodeURIComponent(percentInput || '100')}`}
          >
            더 알아보기
          </CustomLink>
        </Button>
      ) : null}
    </div>
  );
};
